import { lazy, Suspense } from 'react';
import type { RouteObject } from 'react-router-dom';
import AdminLayout from '@/layouts/AdminLayout';
import MobileLayout from '@/layouts/MobileLayout';
import BuyerLayout from '@/layouts/BuyerLayout';
import LoadingSkeleton from '@/components/common/LoadingSkeleton';
import ProtectedRoute from '@/components/common/ProtectedRoute';
import PermissionGate from '@/components/common/PermissionGate';
import PlatformGate from '@/components/common/PlatformGate';

/** 登录页（独立路由，不包 AdminLayout） */
const Login = lazy(() => import('@/pages/Login'));

/** Admin页面懒加载 */
const Dashboard = lazy(() => import('@/pages/admin/Dashboard'));
const ProductCategory = lazy(() => import('@/pages/admin/ProductCategory'));
const TradeOrders = lazy(() => import('@/pages/admin/TradeOrders'));
const FinanceReceivables = lazy(() => import('@/pages/admin/FinanceReceivables'));
const ContainerArchive = lazy(() => import('@/pages/admin/ContainerArchive'));
const ShippingBooking = lazy(() => import('@/pages/admin/ShippingBooking'));
const CustomsClearance = lazy(() => import('@/pages/admin/CustomsClearance'));
const DocumentVerification = lazy(() => import('@/pages/admin/DocumentVerification'));
const IoTAlerts = lazy(() => import('@/pages/admin/IoTAlerts'));
const ClaimRules = lazy(() => import('@/pages/admin/ClaimRules'));
const RiskRating = lazy(() => import('@/pages/admin/RiskRating'));
const BuyerManagement = lazy(() => import('@/pages/admin/BuyerManagement'));
const RouteManage = lazy(() => import('@/pages/admin/RouteManage'));
const NodeConfig = lazy(() => import('@/pages/admin/NodeConfig'));

/** 商品每日价格与价格趋势 */
const ProductPriceList = lazy(() => import('@/pages/admin/ProductPriceList'));
const ProductPriceTrend = lazy(() => import('@/pages/admin/ProductPriceTrend'));
const ProductPriceConfig = lazy(() => import('@/pages/admin/ProductPriceConfig'));

/** 汇率模块 */
const CurrencyConfig = lazy(() => import('@/pages/admin/CurrencyConfig'));
const CurrencyRateList = lazy(() => import('@/pages/admin/CurrencyRateList'));

/** 履约待办中心 */
const TodoCenter = lazy(() => import('@/pages/fulfillment/TodoCenter'));

/** T10 回款里程碑（T9 表单模板编辑器已收口进 NodeConfig，组件由 NodeConfig 直接复用） */
const ReceivableMilestone = lazy(() => import('@/pages/admin/ReceivableMilestone'));

/** IoT 四域页面懒加载 */
const IoTDevices = lazy(() => import('@/pages/admin/IoTDevices'));
const IoTDeviceShadow = lazy(() => import('@/pages/admin/IoTDeviceShadow'));
const IoTDeviceGroups = lazy(() => import('@/pages/admin/IoTDeviceGroups'));
const IoTDeviceTypes = lazy(() => import('@/pages/admin/IoTDeviceTypes'));
const IoTRealtime = lazy(() => import('@/pages/admin/IoTRealtime'));
const IoTHistory = lazy(() => import('@/pages/admin/IoTHistory'));
const IoTAlertRules = lazy(() => import('@/pages/admin/IoTAlertRules'));
const IoTNotifyConfig = lazy(() => import('@/pages/admin/IoTNotifyConfig'));
const IoTAIChat = lazy(() => import('@/pages/admin/IoTAIChat'));
const IoTAIKb = lazy(() => import('@/pages/admin/IoTAIKb'));
const IoTAnomalyDetection = lazy(() => import('@/pages/admin/IoTAnomalyDetection'));
const IoTDataPointTemplate = lazy(() => import('@/pages/admin/IoTDataPointTemplate'));
const IoTAlertCenter = lazy(() => import('@/pages/admin/IoTAlertCenter'));

/** Mobile页面懒加载 */
const TaskPool = lazy(() => import('@/pages/mobile/TaskPool'));
const ScanBindIoT = lazy(() => import('@/pages/mobile/ScanBindIoT'));
const LoadingSeal = lazy(() => import('@/pages/mobile/LoadingSeal'));
const NodeInspection = lazy(() => import('@/pages/mobile/NodeInspection'));
const SignUnbox = lazy(() => import('@/pages/mobile/SignUnbox'));

/** Buyer页面懒加载 */
const OrderPayment = lazy(() => import('@/pages/buyer/OrderPayment'));
const FulfillmentTracking = lazy(() => import('@/pages/buyer/FulfillmentTracking'));
const DocumentReports = lazy(() => import('@/pages/buyer/DocumentReports'));
const ClaimCenter = lazy(() => import('@/pages/buyer/ClaimCenter'));

/** System页面懒加载 */
const SystemConfig = lazy(() => import('@/pages/system/SystemConfig'));
const RBACAudit = lazy(() => import('@/pages/system/RBACAudit'));
const AuditLogPage = lazy(() => import('@/pages/system/AuditLogPage'));
const AICopilot = lazy(() => import('@/pages/system/AICopilot'));
const PublicVerify = lazy(() => import('@/pages/system/PublicVerify'));
const UserManagement = lazy(() => import('@/pages/system/UserManagement'));
const RoleManagement = lazy(() => import('@/pages/system/RoleManagement'));
const MenuManagement = lazy(() => import('@/pages/system/MenuManagement'));
const OrgManagement = lazy(() => import('@/pages/system/OrgManagement'));
const DictConfig = lazy(() => import('@/pages/system/DictConfig'));
const Forbidden = lazy(() => import('@/pages/Forbidden'));

/** P2 平台租户管理页面懒加载 */
const TenantList = lazy(() => import('@/pages/platform/TenantList'));
const TenantCreate = lazy(() => import('@/pages/platform/TenantCreate'));
const TenantDetail = lazy(() => import('@/pages/platform/TenantDetail'));

function LazyPage({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<LoadingSkeleton />}>{children}</Suspense>;
}

export const routes: RouteObject[] = [
  {
    path: '/login',
    element: <LazyPage><Login /></LazyPage>,
  },
  {
    path: '/403',
    element: <LazyPage><Forbidden /></LazyPage>,
  },
  {
    path: '/',
    element: <ProtectedRoute><AdminLayout /></ProtectedRoute>,
    children: [
      { index: true, element: <LazyPage><Dashboard /></LazyPage> },
      { path: 'admin/dashboard', element: <LazyPage><Dashboard /></LazyPage> },
      { path: 'admin/product-category', element: <LazyPage><PermissionGate perm="product:category:list"><ProductCategory /></PermissionGate></LazyPage> },
      { path: 'admin/trade-orders', element: <LazyPage><TradeOrders /></LazyPage> },
      { path: 'admin/finance-receivables', element: <LazyPage><FinanceReceivables /></LazyPage> },
      { path: 'admin/container-archive', element: <LazyPage><ContainerArchive /></LazyPage> },
      { path: 'admin/shipping-booking', element: <LazyPage><ShippingBooking /></LazyPage> },
      { path: 'admin/customs-clearance', element: <LazyPage><CustomsClearance /></LazyPage> },
      { path: 'admin/document-verification', element: <LazyPage><DocumentVerification /></LazyPage> },
      { path: 'admin/claim-rules', element: <LazyPage><ClaimRules /></LazyPage> },
      { path: 'admin/risk-rating', element: <LazyPage><RiskRating /></LazyPage> },
      { path: 'admin/buyer-management', element: <LazyPage><PermissionGate perm="buyer:list"><BuyerManagement /></PermissionGate></LazyPage> },
      { path: 'admin/route', element: <LazyPage><PermissionGate perm="route:list"><RouteManage /></PermissionGate></LazyPage> },
      { path: 'admin/node-config', element: <LazyPage><PermissionGate perm="trade:task:list"><NodeConfig /></PermissionGate></LazyPage> },

      // ===== 履约 T5/T6（PermissionGate 权限门控）=====
      { path: 'admin/fulfillment/todo', element: <LazyPage><PermissionGate perm="trade:task:list"><TodoCenter /></PermissionGate></LazyPage> },

      // ===== 履约 T10（回款里程碑；T9 表单模板编辑器已收口进 NodeConfig）=====
      { path: 'admin/receivable-milestone', element: <LazyPage><PermissionGate perm="trade:task:list"><ReceivableMilestone /></PermissionGate></LazyPage> },

      // ===== 商品每日价格与价格趋势（B8 权限门控）— path & perm 与后端 sys_menu 种子保持一致 =====
      { path: 'admin/product-price-list', element: <LazyPage><PermissionGate perm="product:price:list"><ProductPriceList /></PermissionGate></LazyPage> },
      { path: 'admin/product-price-trend', element: <LazyPage><PermissionGate perm="product:price:trend"><ProductPriceTrend /></PermissionGate></LazyPage> },
      { path: 'admin/product-price-config', element: <LazyPage><PermissionGate perm="product:price:config"><ProductPriceConfig /></PermissionGate></LazyPage> },

      // ===== 汇率模块（B8 权限门控）— path & perm 与后端 sys_menu 种子保持一致 =====
      { path: 'admin/currency-config', element: <LazyPage><PermissionGate perm="currency:config:list"><CurrencyConfig /></PermissionGate></LazyPage> },
      { path: 'admin/currency-rate-list', element: <LazyPage><PermissionGate perm="currency:rate:list"><CurrencyRateList /></PermissionGate></LazyPage> },

      // ===== IoT 四域路由（B8 权限门控）— path & perm 与后端 sys_menu 种子保持一致 =====
      // 设备域
      { path: 'admin/iot-device-ledger', element: <LazyPage><PermissionGate perm="iot:device:list"><IoTDevices /></PermissionGate></LazyPage> },
      // 设备影子：不再作为独立侧边栏菜单，改由设备台账操作列「查看影子」携带 id 进入
      { path: 'admin/iot-device-shadow/:id', element: <LazyPage><PermissionGate perm="iot:device:list"><IoTDeviceShadow /></PermissionGate></LazyPage> },
      { path: 'admin/iot-datapoint-tpl', element: <LazyPage><PermissionGate perm="iot:device:template"><IoTDataPointTemplate /></PermissionGate></LazyPage> },
      { path: 'admin/iot-device-type', element: <LazyPage><PermissionGate perm="iot:device:type:list"><IoTDeviceTypes /></PermissionGate></LazyPage> },
      { path: 'admin/iot-device-group', element: <LazyPage><PermissionGate perm="iot:device:group:list"><IoTDeviceGroups /></PermissionGate></LazyPage> },
      // 数据域
      { path: 'admin/iot-realtime', element: <LazyPage><PermissionGate perm="iot:data:view"><IoTRealtime /></PermissionGate></LazyPage> },
      { path: 'admin/iot-history', element: <LazyPage><PermissionGate perm="iot:data:history"><IoTHistory /></PermissionGate></LazyPage> },
      // 告警域
      { path: 'admin/iot-alerts', element: <LazyPage><PermissionGate perm="iot:alert:list"><IoTAlerts /></PermissionGate></LazyPage> },
      { path: 'admin/iot-alert-center', element: <LazyPage><PermissionGate perm="iot:alert:list"><IoTAlertCenter /></PermissionGate></LazyPage> },
      { path: 'admin/iot-alert-rule', element: <LazyPage><PermissionGate perm="iot:alert:rule:list"><IoTAlertRules /></PermissionGate></LazyPage> },
      { path: 'admin/iot-notify', element: <LazyPage><PermissionGate perm="iot:alert:notify:list"><IoTNotifyConfig /></PermissionGate></LazyPage> },
      // AI 域
      { path: 'admin/iot-ai-chat', element: <LazyPage><PermissionGate perm="iot:ai:chat"><IoTAIChat /></PermissionGate></LazyPage> },
      { path: 'admin/iot-ai-kb', element: <LazyPage><PermissionGate perm="iot:ai:kb:list"><IoTAIKb /></PermissionGate></LazyPage> },
      // 异常检测
      { path: 'admin/iot-anomaly', element: <LazyPage><PermissionGate perm="iot:device:anomaly:list"><IoTAnomalyDetection /></PermissionGate></LazyPage> },

      { path: 'system/config', element: <LazyPage><SystemConfig /></LazyPage> },
      { path: 'system/dict', element: <LazyPage><DictConfig /></LazyPage> },
      { path: 'system/rbac-audit', element: <LazyPage><RBACAudit /></LazyPage> },
      { path: 'system/audit-log', element: <LazyPage><PermissionGate perm="system:audit:list"><AuditLogPage /></PermissionGate></LazyPage> },
      { path: 'system/ai-copilot', element: <LazyPage><AICopilot /></LazyPage> },
      { path: 'system/public-verify', element: <LazyPage><PublicVerify /></LazyPage> },
      // ===== RBAC 4 模块（权限网关包裹） =====
      { path: 'system/user', element: <LazyPage><PermissionGate perm="system:user:list"><UserManagement /></PermissionGate></LazyPage> },
      { path: 'system/role', element: <LazyPage><PermissionGate perm="system:role:list"><RoleManagement /></PermissionGate></LazyPage> },
      { path: 'system/menu', element: <LazyPage><PermissionGate perm="system:menu:list"><MenuManagement /></PermissionGate></LazyPage> },
      { path: 'system/org', element: <LazyPage><PermissionGate perm="system:org:list"><OrgManagement /></PermissionGate></LazyPage> },
      // ===== P2 平台管理（仅平台超管，PlatformGate 门控）=====
      { path: 'platform/tenants', element: <LazyPage><PlatformGate><TenantList /></PlatformGate></LazyPage> },
      { path: 'platform/tenants/new', element: <LazyPage><PlatformGate><TenantCreate /></PlatformGate></LazyPage> },
      { path: 'platform/tenants/:orgCode', element: <LazyPage><PlatformGate><TenantDetail /></PlatformGate></LazyPage> },
    ],
  },
  {
    path: '/mobile',
    element: <MobileLayout />,
    children: [
      { index: true, element: <LazyPage><TaskPool /></LazyPage> },
      { path: 'tasks', element: <LazyPage><TaskPool /></LazyPage> },
      { path: 'scan', element: <LazyPage><PermissionGate perm="iot:device:bind"><ScanBindIoT /></PermissionGate></LazyPage> },
      { path: 'loading-seal', element: <LazyPage><LoadingSeal /></LazyPage> },
      { path: 'inspection', element: <LazyPage><NodeInspection /></LazyPage> },
      { path: 'sign-unbox', element: <LazyPage><SignUnbox /></LazyPage> },
    ],
  },
  {
    path: '/buyer',
    element: <BuyerLayout />,
    children: [
      { index: true, element: <LazyPage><OrderPayment /></LazyPage> },
      { path: 'payment', element: <LazyPage><OrderPayment /></LazyPage> },
      { path: 'tracking', element: <LazyPage><FulfillmentTracking /></LazyPage> },
      { path: 'documents', element: <LazyPage><DocumentReports /></LazyPage> },
      { path: 'claims', element: <LazyPage><ClaimCenter /></LazyPage> },
    ],
  },
];
